'use client';

import React, { useContext } from 'react';
import { cx } from '../../../utils/cx';
import type { MenuItemStateProps, MenuSize } from './menuTypes';
import { MenuContext } from './menuContext';
import { styles } from './menuItemStyles';
import type { PolymorphicProps } from '../../../types/polymorphic';

const MENU_ITEM_DISPLAY_NAME = 'RomaMenuItem';

type MenuItemRootOwnProps = MenuItemStateProps & {
    size?: MenuSize;
    className?: string;
    hasDescription?: boolean;
    hasLeading?: boolean;
    children: React.ReactNode;
};

type MenuItemRootComponentProps<T extends React.ElementType> = PolymorphicProps<MenuItemRootOwnProps, T>;

export type MenuItemRootBaseProps = PolymorphicProps<MenuItemRootOwnProps>;

/**
 * Renders the shared outer shell for menu items across structured and custom variants.
 */
export const MenuItemBase = <T extends React.ElementType = 'div'>(props: MenuItemRootComponentProps<T>) => {
    const {
        as,
        size: sizeProp,
        className,
        hasDescription,
        hasLeading,
        isSelected,
        hasSubmenu,
        isOpen,
        isDisabled,
        isFocused,
        isFocusVisible,
        isHovered,
        children,
        ...restProps
    } = props;

    const { size: contextSize } = useContext(MenuContext);
    const size = sizeProp ?? contextSize;
    const sizeStyle = styles.sizes[size];
    const Component = as ?? 'div';

    return (
        <Component
            {...restProps}
            data-size={size}
            data-selected={isSelected || undefined}
            data-has-submenu={hasSubmenu || undefined}
            data-open={(isOpen && !isDisabled) || undefined}
            data-has-description={hasDescription || undefined}
            data-has-leading={hasLeading || undefined}
            data-disabled={isDisabled || undefined}
            data-focused={(isFocused && !isDisabled) || undefined}
            data-focus-visible={(isFocusVisible && !isDisabled) || undefined}
            data-hovered={(isHovered && !isDisabled) || undefined}
            className={cx(MENU_ITEM_DISPLAY_NAME, styles.common.shell, sizeStyle.shell, className)}
        >
            {children}
        </Component>
    );
};

MenuItemBase.displayName = MENU_ITEM_DISPLAY_NAME;
